import {
	IDataListColumn,
	IconLinkButton,
	PaginatedAutoDataList,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { userService } from "../shared/api/userService";
import { UserIndexResource } from "../shared/types";
import { UserContextMenu } from "./UserContextMenu";
import { useGetProfileContactView, useUserListColumns } from "./userListHooks";

export const UserList: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const { profileContactView } = useGetProfileContactView();
	const columns = useUserListColumns(profileContactView);

	const handleBlock = useCallback(
		async (user: UserIndexResource) => {
			const response = await userService.block(user.id);
			if (isSuccess(response)) {
				void queryClient.invalidateQueries({ queryKey: userService.indexKey });
				showToast({
					type: "success",
					title: t("Block user"),
					body: t("User was successfully blocked."),
				});
			}
		},
		[showToast, t, queryClient],
	);

	const handleUnblock = useCallback(
		async (user: UserIndexResource) => {
			const response = await userService.unblock(user.id);
			if (isSuccess(response)) {
				void queryClient.invalidateQueries({ queryKey: userService.indexKey });
				showToast({
					type: "success",
					title: t("Unblock user"),
					body: t("User was successfully unblocked."),
				});
			}
		},
		[showToast, t, queryClient],
	);

	const dataListColumns: IDataListColumn<UserIndexResource>[] = [
		...columns,
		{
			heading: "",
			type: "actions",
			size: "max-content",
			action: (user) => (
				<UserContextMenu user={user} onBlock={() => handleBlock(user)} onUnblock={() => handleUnblock(user)} />
			),
		},
	];

	return (
		<PaginatedAutoDataList
			name="users"
			columns={dataListColumns}
			empty={t("No users available")}
			heading={t("Users")}
			initialSearch={{
				search: "",
				searchFields: [
					{ name: "firstName", label: t("First name") },
					{ name: "lastName", label: t("Last name") },
					{ name: "email", label: t("E-Mail") },
				],
			}}
			listFetchConfig={laravelPaginatedListFetchConfig(userService.listKey, userService.index)}
			onRowClick={(value) => navigate(`/users/${value.id}/edit`)}
			headerActions={
				<IconLinkButton to="/users/new" color="primary" size="small" icon="plus" title={t("Create user")} />
			}
		/>
	);
};
